import React from 'react';
import type { SortDescriptor } from 'react-aria-components';
import {
    ATTR_TYPE_COLUMN,
    getPaginationWindow,
    TYPES_PAGE_SIZE,
    type AttributeTypeRow,
} from '../attributesMeta';

const DEFAULT_SORT: SortDescriptor = {
    column: ATTR_TYPE_COLUMN.name,
    direction: 'ascending',
};

/** The value a sortable column sorts on (all sortable columns are strings). */
const sortField = (
    row: AttributeTypeRow,
    column: SortDescriptor['column'],
): string => {
    if (column === ATTR_TYPE_COLUMN.slug) {
        return row.slug;
    }
    if (column === ATTR_TYPE_COLUMN.inputKind) {
        return row.inputKind;
    }
    return row.name;
};

/**
 * Global sort + client-side pagination for the types table. The full dataset
 * is sorted by the active `sortDescriptor` *before* slicing, so page 1 always
 * holds the first rows in sorted order (the DataGrid receives only the slice,
 * so its own sorting would otherwise apply to that page alone). `page` is
 * clamped to `[1, totalPages]`; changing the page size resets to page 1.
 */
export const useTypesTable = (data: AttributeTypeRow[]) => {
    const [page, setPage] = React.useState(1);
    const [rowsPerPage, setRowsPerPage] = React.useState(TYPES_PAGE_SIZE);
    const [sortDescriptor, setSortDescriptor] =
        React.useState<SortDescriptor>(DEFAULT_SORT);

    const sortedData = React.useMemo(() => {
        const direction = sortDescriptor.direction === 'descending' ? -1 : 1;
        return [...data].sort(
            (a, b) =>
                sortField(a, sortDescriptor.column).localeCompare(
                    sortField(b, sortDescriptor.column),
                ) * direction,
        );
    }, [data, sortDescriptor]);

    const totalItems = sortedData.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
    // Clamp to [1, totalPages] so an out-of-range page can never produce a
    // negative slice offset (which would read from the end of the array).
    const safePage = Math.min(Math.max(1, page), totalPages);

    const pageItems = React.useMemo(() => {
        const start = (safePage - 1) * rowsPerPage;
        return sortedData.slice(start, start + rowsPerPage);
    }, [rowsPerPage, safePage, sortedData]);

    const pageWindow = React.useMemo(
        () => getPaginationWindow(totalPages, safePage),
        [safePage, totalPages],
    );

    const onPageChange = React.useCallback((next: number) => {
        setPage(next);
    }, []);

    const onRowsPerPageChange = React.useCallback((next: number) => {
        setRowsPerPage(next);
        setPage(1);
    }, []);

    const onSortChange = React.useCallback((next: SortDescriptor) => {
        setSortDescriptor(next);
    }, []);

    return {
        pageItems,
        page: safePage,
        totalPages,
        totalItems,
        pageWindow,
        rowsPerPage,
        sortDescriptor,
        onPageChange,
        onRowsPerPageChange,
        onSortChange,
    };
};
